import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
    IconChevronDownCircle,
    IconChevronUpCircle,
    Text,
    Flex,
    Spacer,
    Paragraph,
} from '@zillow/constellation';

import {
    IconCheck,
    IconRadioDeselected,
    IconError,
    Tag,
    IconifiedList,
} from '@zillow/zrs-comet';

import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';
import { GA_EVENTS, GA_PREFIX } from 'app/shared/constants/AnalyticsPaymentsModuleConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import { getTagTypeByKey, STATUS_KEY } from 'app/shared/utils/statusTagUtils';
import { StyledNoWrapButton, StyledTextButtonNoUnderline } from 'app/shared/StyledComponents/StyledComponents';

import { StyledFlex, StyledFlexPointerCursor } from './StyledComponents';
import InlineStyledText from '../InlineStyledText';

const { PAYMENT_SUMMARY } = STATUS_KEY;

const LIST_FONT_MAP = {
    normal: {
        fontType: 'smallbody',
        fontColor: 'gray600',
    },
    emphasize: {
        fontType: 'smalllabel',
        fontColor: 'gray600',
    },
    lowlight: {
        fontType: 'smallbody',
        fontColor: 'textMedium',
    },
};

const LIST_ICON_MAP = {
    checked: <IconCheck fontColor="green300" />,
    unchecked: <IconRadioDeselected fontColor="borderLight" />,
    alert: <IconError fontColor="red400" />,
};

const CTA_BUTTON_MAP = {
    primary_button: 'primary',
    secondary_button: 'secondary',
};

const DROP_DOWN = {
    COLLAPSE: 'collapse',
    EXPAND: 'expand',
};

const {
    EXPAND_PAYMENT_DETAILS_CLICK,
    COLLAPSE_PAYMENT_DETAILS_CLICK,
    PAYMENT_CTA_CLICK,
} = GA_EVENTS;

const {
    COLLAPSE_PAYMENT_DETAILS,
    EXPAND_PAYMENT_DETAILS,
} = GA_PREFIX;

const PaymentTracker = ({
    paymentType,
    statusText,
    statusType,
    trackerStates,
    ctaButtons,
    disclaimer,
}) => {
    const dispatch = useDispatch();
    const [isDropdownEnabled, setDropdownEnabled] = useState(false);

    const gaFormattedPaymentType = stringHelper.startCaseRemoveSpace(paymentType);
    const gaFormattedPaymentStatus = stringHelper.startCaseRemoveSpace(statusText.toLowerCase());

    const getIconifiedListItems = list => list.map(({ messageText, icon, messageStyle }) => ({
        icon: LIST_ICON_MAP[icon],
        primaryContent: <Text {...LIST_FONT_MAP[messageStyle]}>{ messageText }</Text>,
    }));

    const triggerGAEvents = (direction) => {
        if (direction === DROP_DOWN.EXPAND) {
            dispatch(analyticsUtils.track.GAEventV2({
                ...EXPAND_PAYMENT_DETAILS_CLICK,
                action: `${EXPAND_PAYMENT_DETAILS}
                    ${DELIMITER}${gaFormattedPaymentType}
                    ${DELIMITER}${gaFormattedPaymentStatus}`,
            }));
        } else if (direction === DROP_DOWN.COLLAPSE) {
            dispatch(analyticsUtils.track.GAEventV2({
                ...COLLAPSE_PAYMENT_DETAILS_CLICK,
                action: `${COLLAPSE_PAYMENT_DETAILS}
                    ${DELIMITER}${gaFormattedPaymentType}
                    ${DELIMITER}${gaFormattedPaymentStatus}`,
            }));
        }
    };

    const handleCTAClick = (event) => {
        const action = `${stringHelper.startCaseRemoveSpace(event.target.textContent)}
            ${DELIMITER}${gaFormattedPaymentType}
            ${DELIMITER}${gaFormattedPaymentStatus}`;

        dispatch(analyticsUtils.track.GAEventV2({
            ...PAYMENT_CTA_CLICK,
            action,
        }));
    };

    const handleDropdownChange = (direction = '') => {
        triggerGAEvents(direction);
        setDropdownEnabled(!isDropdownEnabled);
    };

    return (
        <div className="PaymentTracker" data-test="connected-payments-payment-tracker">
            <Flex
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                paddingBottom={isDropdownEnabled ? 'md' : null}
            >
                <Tag size="sm" appearance={getTagTypeByKey(PAYMENT_SUMMARY, statusType)}>{statusText}</Tag>
                <StyledFlexPointerCursor display="flex">
                    <StyledTextButtonNoUnderline
                        onClick={() => setDropdownEnabled(!isDropdownEnabled)}
                        fontType="bodySmallHeading"
                        paddingRight="xs"
                        data-test="payment-tracker-dropdown-toggle"
                        applyOnHover={true}
                    >
                        View details
                        {' '}
                    </StyledTextButtonNoUnderline>
                    {isDropdownEnabled ? (
                        <IconChevronUpCircle
                            size="sm"
                            fontColor="brand"
                            onClick={() => { handleDropdownChange(DROP_DOWN.COLLAPSE); }}
                        />
                    ) : (
                        <IconChevronDownCircle
                            size="sm"
                            fontColor="brand"
                            onClick={() => { handleDropdownChange(DROP_DOWN.EXPAND); }}
                        />
                    )}
                </StyledFlexPointerCursor>
            </Flex>
            {isDropdownEnabled && (
                <div data-test="payment-tracker-dropdown">
                    {trackerStates.length ?
                        (
                            <Spacer paddingBottom="xs">
                                <IconifiedList
                                    backgroundColor="white"
                                    verticalLineColor="borderLight"
                                    iconSize="md"
                                    listItems={getIconifiedListItems(trackerStates)}
                                />
                            </Spacer>
                        )
                        : null
                    }
                    {disclaimer ?
                        (
                            <Paragraph
                                fontType="bodySmall"
                                fontColor="textMedium"
                                paddingBottom={ctaButtons.length ? 'md' : 0}
                            >
                                <InlineStyledText fontColor="textMedium" styledTextList={disclaimer} />
                            </Paragraph>
                        )
                        : null
                    }
                    {ctaButtons.length ?
                        (
                            <StyledFlex display="flex">
                                {ctaButtons.map(({ text, styling, url }) => (
                                    <Flex key={url} className="PaymentTracker-CTA-Button" flex="1">
                                        <StyledNoWrapButton
                                            forwardedAs="a"
                                            href={url}
                                            fluid={true}
                                            size="sm"
                                            buttonType={CTA_BUTTON_MAP[styling]}
                                            onClick={handleCTAClick}
                                        >
                                            {text}
                                        </StyledNoWrapButton>
                                    </Flex>
                                ))}
                            </StyledFlex>
                        )
                        : null
                    }
                </div>
            )}
        </div>
    );
};

PaymentTracker.propTypes = {
    paymentType: PropTypes.string.isRequired,
    statusText: PropTypes.string.isRequired,
    statusType: PropTypes.string.isRequired,
    trackerStates: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.oneOf(['checked', 'unchecked', 'alert']).isRequired,
        message: PropTypes.string.isRequired,
        styling: PropTypes.oneOf([
            'normal',
            'emphasize',
            'lowLight',
            'emphasize',
        ]),
    })).isRequired,
    ctaButtons: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        styling: PropTypes.string.isRequired,
    })),
    disclaimer: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string.isRequired,
        styling: PropTypes.string,
        url: PropTypes.string,
    })),
};

PaymentTracker.defaultProps = {
    ctaButtons: [],
    disclaimer: [],
};

export default PaymentTracker;
